<template>
  <div class="resumen">
    <!-- Cargando -->
    <div v-if="cargando" class="estado-centro">
      <div class="spinner"></div>
      <p>Cargando resumen…</p>
    </div>

    <!-- Sin evento -->
    <div v-else-if="!data" class="estado-centro">
      <i class="bi bi-calendar-x" style="font-size: 2.5rem; opacity: 0.3"></i>
      <p>No hay evento activo.</p>
    </div>

    <template v-else>
      <!-- ── HEADER ── -->
      <div class="page-header">
        <div>
          <h1 class="page-title-wrap">{{ data.nombre }}</h1>
          <p class="page-sub">
            <i class="bi bi-calendar3"></i> {{ formatFecha(data.fecha_evento) }}
            &nbsp;·&nbsp;
            <i class="bi bi-clock"></i> {{ formatHora(data.hora_inicio) }}
            &nbsp;·&nbsp;
            <i class="bi bi-geo-alt"></i> {{ data.ubicacion }}
          </p>
        </div>
        <div class="refresh-tag" :class="{ girando: cargando }">
          <i class="bi bi-arrow-clockwise" @click="cargar" style="cursor: pointer"></i>
          {{ ultimaActualizacion }}
        </div>
      </div>

      <!-- ── MÉTRICAS PRINCIPALES ── -->
      <div class="metricas-grid">
        <!-- Top peleadores por boletos vendidos (suma de todos los tipos) -->
        <div class="metrica-card metrica-top-peleadores">
          <div class="metrica-top-header">
            <p class="metrica-label">Top peleadores</p>
            <select
              v-if="peleadoresTop.length > 5"
              class="filas-selector filas-selector--mini"
              v-model.number="filasTopPeleadores"
            >
              <option
                v-for="opcion in opcionesFilas(peleadoresTop.length)"
                :key="opcion.value"
                :value="opcion.value"
              >
                {{ opcion.label }}
              </option>
            </select>
          </div>

          <div v-if="cargandoTopPeleadores" class="mini-spinner-wrap">
            <div class="spinner spinner--mini"></div>
          </div>

          <p v-else-if="peleadoresTop.length === 0" class="metrica-top-vacio">
            Sin boletos con peleador elegido aún.
          </p>

          <div v-else class="metrica-top-lista">
            <div
              v-for="(p, i) in peleadoresTopVisibles"
              :key="p.peleador_id"
              class="populares-fila populares-fila--mini"
            >
              <span class="populares-rank">{{ i + 1 }}</span>

              <div class="populares-avatar populares-avatar--mini">
                <img v-if="p.foto" :src="p.foto" :alt="p.nombre" />
                <span v-else>{{ inicial(p.nombre) }}</span>
              </div>

              <div class="populares-nombre-wrap">
                <p class="populares-nombre populares-nombre--mini">
                  {{ p.nombre }} {{ p.apellido }}
                </p>
                <div class="populares-barra-track">
                  <div
                    class="populares-barra-fill"
                    :style="{
                      width:
                        pctBarra(p.boletos_total, peleadoresTop[0].boletos_total) + '%',
                    }"
                  ></div>
                </div>
              </div>

              <span class="populares-boletos populares-boletos--mini">{{
                p.boletos_total
              }}</span>
            </div>
          </div>
        </div>

        <!-- Cupo + Boletos combinados: una sola card, dos renglones -->
        <div class="metrica-card metrica-combo">
          <div class="combo-fila">
            <p class="metrica-label">Cupo ocupado</p>
            <div class="cupo-numero cupo-numero--combo">
              {{ data.porcentaje_cupo }}<span class="cupo-sym">%</span>
            </div>
            <div class="cupo-track">
              <div
                class="cupo-fill"
                :style="{ width: data.porcentaje_cupo + '%' }"
                :class="colorCupo"
              >
                <span class="cupo-shine"></span>
              </div>
            </div>
            <p class="metrica-hint" v-if="data.porcentaje_cupo >= 75">
              <i class="bi bi-lightning-fill"></i> Últimos lugares disponibles
            </p>
          </div>

          <div class="combo-divider"></div>

          <div class="combo-fila">
            <p class="metrica-label">Boletos</p>
            <p class="metrica-valor metrica-valor--combo">
              {{ data.boletos_vendidos }}
              <span class="metrica-de">/ {{ data.boletos_total_cupo }}</span>
            </p>
            <p class="metrica-sub">vendidos</p>
            <div class="cupo-track" style="margin-top: 10px">
              <div class="cupo-fill" :style="{ width: pctBoletos + '%' }">
                <span class="cupo-shine"></span>
              </div>
            </div>
            <div class="boletos-footer">
              <span class="boletos-disp">
                <i class="bi bi-ticket-perforated"></i>
                {{ data.boletos_disponibles }} disponibles
              </span>
              <span class="boletos-pct">{{ pctBoletos }}%</span>
            </div>
          </div>
        </div>

        <!-- Peleadores -->
        <div class="metrica-card">
          <p class="metrica-label">Peleadores</p>
          <p class="metrica-valor">{{ data.peleadores_total }}</p>
          <div class="estatus-lista">
            <div class="estatus-item">
              <span class="dot confirmado"></span>
              <span class="estatus-texto">Confirmados</span>
              <span class="estatus-num">{{ data.peleadores_confirmados }}</span>
            </div>
            <div class="estatus-item">
              <span class="dot pendiente"></span>
              <span class="estatus-texto">Pendientes</span>
              <span class="estatus-num">{{ data.peleadores_pendientes }}</span>
            </div>
            <div class="estatus-item">
              <span class="dot rechazado"></span>
              <span class="estatus-texto">Rechazados</span>
              <span class="estatus-num">{{ data.peleadores_rechazados }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── PELEADORES MÁS ELEGIDOS POR TIPO DE BOLETO ── -->
      <div class="seccion-header">
        <h2 class="seccion-titulo">Peleadores más elegidos</h2>
        <p class="seccion-sub">
          Por cada tipo de boleto, a qué peleador eligieron ver los compradores.
        </p>
      </div>

      <!-- Cargando peleadores populares -->
      <div v-if="cargandoPopulares" class="estado-centro estado-centro--compacto">
        <div class="spinner"></div>
        <p>Cargando…</p>
      </div>

      <!-- Sin tipos de boleto activos -->
      <div v-else-if="peleadoresPopulares.length === 0" class="vacio-populares">
        <i class="bi bi-people"></i>
        <p>No hay tipos de boleto activos para mostrar.</p>
      </div>

      <div v-else class="populares-grid">
        <div
          v-for="tipo in peleadoresPopularesOrdenados"
          :key="tipo.tipo_boleto_id"
          class="populares-card"
          :class="{ 'populares-card--vacia': tipo.peleadores.length === 0 }"
        >
          <!-- Card colapsada: sin peleador elegido todavía en este tipo -->
          <div v-if="tipo.peleadores.length === 0" class="populares-vacia-fila">
            <span class="populares-vacia-nombre">{{ tipo.tipo_boleto_nombre }}</span>
            <span class="populares-vacia-msg">Sin peleador elegido aún</span>
          </div>

          <!-- Card completa: con ranking -->
          <template v-else>
            <div class="populares-card-header">
              <div>
                <p class="populares-tipo-nombre">{{ tipo.tipo_boleto_nombre }}</p>
                <p class="populares-tipo-total">
                  {{ tipo.total_boletos_con_peleador }} boleto{{
                    tipo.total_boletos_con_peleador === 1 ? "" : "s"
                  }}
                  con peleador elegido
                </p>
              </div>

              <!-- Selector de filas -->
              <select
                v-if="tipo.peleadores.length > 5"
                class="filas-selector"
                v-model.number="filasPorTipo[tipo.tipo_boleto_id]"
              >
                <option
                  v-for="opcion in opcionesFilas(tipo.peleadores.length)"
                  :key="opcion.value"
                  :value="opcion.value"
                >
                  {{ opcion.label }}
                </option>
              </select>
            </div>

            <div class="populares-lista">
              <div
                v-for="(p, i) in filasVisibles(tipo)"
                :key="p.peleador_id"
                class="populares-fila"
              >
                <span class="populares-rank">{{ i + 1 }}</span>

                <div class="populares-avatar">
                  <img v-if="p.foto" :src="p.foto" :alt="p.nombre" />
                  <span v-else>{{ inicial(p.nombre) }}</span>
                </div>

                <div class="populares-nombre-wrap">
                  <p class="populares-nombre">{{ p.nombre }} {{ p.apellido }}</p>
                  <div class="populares-barra-track">
                    <div
                      class="populares-barra-fill"
                      :style="{
                        width: pctBarra(p.boletos, tipo.peleadores[0].boletos) + '%',
                      }"
                    ></div>
                  </div>
                </div>

                <span class="populares-boletos">{{ p.boletos }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import {
  getResumenDashboard,
  getPeleadoresPopularesDashboard,
  getPeleadoresTopBoletosDashboard,
} from "@/utils/api_dashboard";

const data = ref(null);
const cargando = ref(false);
const ultimaActualizacion = ref("");

const peleadoresPopulares = ref([]);
const cargandoPopulares = ref(false);

// Cuántas filas mostrar por card, por tipo_boleto_id. Default 5.
const filasPorTipo = reactive({});

// Top global de peleadores por boletos vendidos (suma de todos los tipos).
// El backend ya devuelve el top 15; esto solo controla cuántas se pintan.
const peleadoresTop = ref([]);
const cargandoTopPeleadores = ref(false);
const filasTopPeleadores = ref(5);

async function cargar() {
  cargando.value = true;
  cargandoPopulares.value = true;
  cargandoTopPeleadores.value = true;
  try {
    const [resumen, populares, topPeleadores] = await Promise.all([
      getResumenDashboard(),
      getPeleadoresPopularesDashboard(),
      getPeleadoresTopBoletosDashboard(),
    ]);
    data.value = resumen;
    peleadoresPopulares.value = populares;
    peleadoresTop.value = topPeleadores;

    // Inicializar el selector de filas de cards nuevas (no pisa lo que el
    // usuario ya haya elegido en cards existentes).
    for (const tipo of populares) {
      if (!(tipo.tipo_boleto_id in filasPorTipo)) {
        filasPorTipo[tipo.tipo_boleto_id] = 5;
      }
    }

    const now = new Date();
    ultimaActualizacion.value = `Actualizado ${now.getHours()}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
  } catch (e) {
    console.error("[ResumenEvento] Error cargando resumen:", e);
  } finally {
    cargando.value = false;
    cargandoPopulares.value = false;
    cargandoTopPeleadores.value = false;
  }
}

const pctBoletos = computed(() => {
  if (!data.value?.boletos_total_cupo) return 0;
  return Math.round((data.value.boletos_vendidos / data.value.boletos_total_cupo) * 100);
});

const colorCupo = computed(() => {
  const p = Number(data.value?.porcentaje_cupo ?? 0);
  if (p >= 90) return "fill-rojo";
  if (p >= 75) return "fill-amarillo";
  return "";
});

function formatFecha(f) {
  if (!f) return "—";
  const [y, m, d] = f.split("-");
  const meses = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  return `${parseInt(d)} ${meses[parseInt(m) - 1]} ${y}`;
}

function formatHora(h) {
  if (!h) return "—";
  const [hh, mm] = h.split(":");
  const hr = parseInt(hh);
  return `${hr > 12 ? hr - 12 : hr}:${mm} ${hr >= 12 ? "PM" : "AM"}`;
}

const peleadoresPopularesOrdenados = computed(() =>
  [...peleadoresPopulares.value].sort((a, b) => {
    const aVacio = a.peleadores.length === 0;
    const bVacio = b.peleadores.length === 0;
    if (aVacio !== bVacio) return aVacio ? 1 : -1; // vacías al final
    return b.total_boletos_con_peleador - a.total_boletos_con_peleador; // más votadas primero
  })
);

// El backend ya viene ordenado desc y limitado a 15; aquí solo se recorta
// a lo que el selector Top 5 / Top 10 / Todos pida.
const peleadoresTopVisibles = computed(() =>
  peleadoresTop.value.slice(0, filasTopPeleadores.value)
);

// ── Peleadores más elegidos ────────────────────────────────────────────────
function inicial(nombre) {
  return nombre?.charAt(0)?.toUpperCase() || "?";
}

// Opciones del selector de filas: 5, 10 (si aplica) y "Todos".
function opcionesFilas(total) {
  const opciones = [{ value: 5, label: "Top 5" }];
  if (total > 5) opciones.push({ value: 10, label: "Top 10" });
  if (total > 10) opciones.push({ value: total, label: `Todos (${total})` });
  return opciones;
}

function filasVisibles(tipo) {
  const n = filasPorTipo[tipo.tipo_boleto_id] ?? 5;
  return tipo.peleadores.slice(0, n);
}

// Barra proporcional al líder del ranking (evita divisiones por cero).
function pctBarra(boletos, maxBoletos) {
  if (!maxBoletos) return 0;
  return Math.max(6, Math.round((boletos / maxBoletos) * 100));
}

onMounted(cargar);
</script>

<style scoped>
.resumen {
  padding: 32px;
  font-family: "Inter", sans-serif;
  color: #fff;
  background: #0f1520;
  min-height: 100%;
}
@media (max-width: 768px) {
  .resumen {
    padding: 20px;
  }
}
@media (max-width: 480px) {
  .resumen {
    padding: 15px;
  }
}

/* ── Estado ── */
.estado-centro {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  gap: 14px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
}
.estado-centro--compacto {
  height: auto;
  padding: 40px 0;
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: #f3bc2c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Header ── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
  flex-wrap: wrap;
  gap: 12px;
}
.page-title-wrap {
  color: #f3bc2c;
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 6px;
}
.page-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.page-sub i {
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
}

.refresh-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.35);
  white-space: nowrap;
}
.refresh-tag.girando i {
  animation: spin 0.8s linear infinite;
}
@media (max-width: 480px) {
  .page-header {
    margin-bottom: 20px;
  }
  .page-title-wrap {
    font-size: 18px;
  }
  .page-sub {
    font-size: 12px;
  }
}

/* ── Grid de métricas ── */
.metricas-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 40px;
}
@media (max-width: 900px) {
  .metricas-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 560px) {
  .metricas-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 28px;
  }
}

/* ── Tarjeta base ── */
.metrica-card {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
@media (max-width: 480px) {
  .metrica-card {
    padding: 16px;
  }
}
.metrica-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.35);
  margin: 0;
}
.metrica-valor {
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
  margin: 0;
  color: #fff;
}
.metrica-valor--combo {
  font-size: 28px;
}
.metrica-de {
  font-size: 18px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.3);
}
.metrica-sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}
.metrica-hint {
  font-size: 11px;
  font-weight: 600;
  color: #facc15;
  margin: 4px 0 0;
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── Cupo + Boletos combinados (una sola card, dos renglones) ── */
.metrica-combo {
  gap: 0;
}
.combo-fila {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 0;
}
.combo-fila:first-child {
  padding-top: 0;
}
.combo-fila:last-child {
  padding-bottom: 0;
}
.combo-divider {
  height: 1px;
  background: rgba(255, 255, 255, 0.08);
  margin: 2px 0;
}
.cupo-numero {
  font-size: 42px;
  font-weight: 800;
  line-height: 1;
  color: #fff;
}
.cupo-numero--combo {
  font-size: 32px;
}
.cupo-sym {
  font-size: 22px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
}

.cupo-track {
  height: 7px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.cupo-fill {
  position: relative;
  height: 100%;
  background: linear-gradient(90deg, #0066cc, #00aaff);
  border-radius: 4px;
  overflow: hidden;
  transition: width 0.6s ease-out;
}
.cupo-fill.fill-amarillo {
  background: linear-gradient(90deg, #d97706, #f59e0b);
}
.cupo-fill.fill-rojo {
  background: linear-gradient(90deg, #b91c1c, #ef4444);
}

.cupo-shine {
  position: absolute;
  top: 0;
  left: -40%;
  width: 35%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shine 2.5s linear infinite;
}
@keyframes shine {
  0% {
    left: -40%;
  }
  100% {
    left: 130%;
  }
}

/* ── Estatus peleadores ── */
.estatus-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}
.estatus-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot.confirmado {
  background: #4ade80;
}
.dot.pendiente {
  background: #facc15;
}
.dot.rechazado {
  background: #f87171;
}
.estatus-texto {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  flex: 1;
}
.estatus-num {
  font-size: 15px;
  font-weight: 700;
  color: #fff;
}

/* ── Boletos ── */
.boletos-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
}
.boletos-disp {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 5px;
}
.boletos-pct {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
}

/* ── Top peleadores por boletos vendidos (card del primer renglón) ── */
.metrica-top-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}
.filas-selector--mini {
  font-size: 10.5px;
  font-weight: 600;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 3px 6px;
  background: #0f1520;
  cursor: pointer;
  flex-shrink: 0;
}
.filas-selector--mini:focus {
  outline: none;
  border-color: #f3bc2c;
}
.metrica-top-vacio {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.35);
  margin: 8px 0 0;
}
.metrica-top-lista {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
  max-height: 182px;
  overflow-y: auto;
  padding-right: 4px;
}
.metrica-top-lista::-webkit-scrollbar {
  width: 5px;
}
.metrica-top-lista::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}
.mini-spinner-wrap {
  display: flex;
  justify-content: center;
  padding: 14px 0;
}
.spinner--mini {
  width: 22px;
  height: 22px;
  border-width: 2px;
}
.populares-fila--mini {
  gap: 8px;
}
.populares-avatar--mini {
  width: 26px;
  height: 26px;
  font-size: 10.5px;
}
.populares-nombre--mini {
  font-size: 11.5px;
  margin: 0 0 3px;
}
.populares-boletos--mini {
  font-size: 12.5px;
  min-width: 18px;
}

/* ── Sección: peleadores más elegidos ── */
.seccion-header {
  margin-bottom: 16px;
}
.seccion-titulo {
  font-size: 17px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 4px;
}
.seccion-sub {
  font-size: 12.5px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
}

.vacio-populares {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 13px;
  background: #060d1a;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  border-radius: 14px;
}
.vacio-populares i {
  font-size: 1.8rem;
  opacity: 0.4;
}

.populares-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  align-items: start;
}
/*
 * El minmax(320px, …) de arriba es lo que provocaba que las cards se
 * salieran del contenedor en móvil: si el viewport (menos el padding de
 * .resumen) es más angosto que 320px, el grid igual reserva esos 320px
 * mínimos y desborda. Por debajo de 700px forzamos una sola columna sin
 * el mínimo fijo, así cada card ocupa el 100% del ancho disponible.
 */
@media (max-width: 700px) {
  .populares-grid {
    grid-template-columns: 1fr;
  }
}

.populares-card {
  background: #060d1a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 20px;
  min-width: 0;
}
@media (max-width: 480px) {
  .populares-card {
    padding: 16px;
  }
}

.populares-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.populares-tipo-nombre {
  font-size: 14.5px;
  font-weight: 800;
  color: #fff;
  margin: 0 0 2px;
}
.populares-tipo-total {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.4);
  margin: 0;
}

.filas-selector {
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 7px;
  padding: 5px 8px;
  background: #0f1520;
  cursor: pointer;
  flex-shrink: 0;
}
.filas-selector:focus {
  outline: none;
  border-color: #f3bc2c;
}

/* Card colapsada: sin peleador elegido todavía en este tipo de boleto */
.populares-card--vacia {
  padding: 14px 20px;
}
.populares-vacia-fila {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.populares-vacia-nombre {
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.populares-vacia-msg {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  flex-shrink: 0;
}

.populares-lista {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 262px;
  overflow-y: auto;
  padding-right: 6px;
}
.populares-lista::-webkit-scrollbar {
  width: 5px;
}
.populares-lista::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

.populares-fila {
  display: flex;
  align-items: center;
  gap: 10px;
}

.populares-rank {
  width: 16px;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  text-align: center;
}

.populares-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(243, 188, 44, 0.15);
  color: #f3bc2c;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}
.populares-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.populares-nombre-wrap {
  flex: 1;
  min-width: 0;
}
.populares-nombre {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.populares-barra-track {
  height: 5px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 3px;
  overflow: hidden;
}
.populares-barra-fill {
  height: 100%;
  background: linear-gradient(90deg, #d99a00, #f3bc2c);
  border-radius: 3px;
  transition: width 0.5s ease-out;
}

.populares-boletos {
  font-size: 14px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  min-width: 22px;
  text-align: right;
}
</style>
